import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import { type AppError, appointmentKeys, request } from '@/shared/api';

import type { Appointment } from '../model/types';
import { myAppointmentsSchema, toAppointment } from './schema';

/**
 * «نوبت‌های من» — the signed-in account's confirmed appointments.
 *
 * The request carries no identifier: whose appointments these are is the
 * session's answer, decided server-side. `enabled` is the caller's, because the
 * page renders a sign-in gate for a visitor and must not fire a 401 behind it.
 *
 * `staleTime` is short but not zero. This list changes for reasons that happen
 * off this screen — a payment settling in another tab, the clinic cancelling a
 * day — so a refetch on focus is what makes returning to the tab tell the
 * truth, while thirty seconds keeps flicking between tabs from refetching on
 * every touch.
 */
export function useMyAppointments(
  options: {
    readonly enabled?: boolean;
    /** The tab bar's watch for a doctor's reply; the page itself does not poll. */
    readonly refetchInterval?: number;
    /** «مشاوره» asks for the phone consultations; everything else, the visits. */
    readonly kind?: 'in_person' | 'consultation';
  } = {},
): UseQueryResult<readonly Appointment[], AppError> {
  const kind = options.kind ?? 'in_person';

  return useQuery<readonly Appointment[], AppError>({
    queryKey: appointmentKeys.mine(kind),
    queryFn: async ({ signal }): Promise<readonly Appointment[]> => {
      const { items } = await request(kind === 'in_person' ? '/appointments' : `/appointments?kind=${kind}`, {
        schema: myAppointmentsSchema,
        signal,
        auth: 'account',
      });

      return items.map(toAppointment);
    },
    enabled: options.enabled ?? true,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: options.refetchInterval ?? false,
  });
}
